import { PanelLeft, PanelLeftClose } from 'lucide-react';
import React, { useState, useEffect, useContext } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/auth.context';
import { useNavigationItems } from '../hooks/useNavigationItems';
import { useSidebarContext, type SidebarMode } from '../context/SidebarContext';
import { REGULAR_NAV_CONFIG } from '../config/navigation';
import ProfileModal from './ProfileModal';
import { TaskGuideWidget } from './TaskGuideWidget';
import SubscriptionBadge from './subscription/SubscriptionBadge';
import { UserAvatar } from './common/UserAvatar';
import { getUserDisplayName, type UserDisplayData } from '../lib/utils/userDisplay';

/**
 * Navigation item structure supporting nested items
 * Extends EvaluatedNavItem from navigation system
 */
export interface NavItem {
  label: string;
  path?: string;
  icon: React.ReactNode;
  items?: NavItem[];
  badgeCount?: number;
  badgeText?: string;
  badgeVariant?: 'count' | 'label' | 'dot';
  badgeColor?: string;
}

const TILE_BASE =
  'flex items-center justify-center size-9 shrink-0 rounded-lg inset-ring transition-all duration-200';
const TILE_INACTIVE =
  'bg-zinc-100 inset-ring-1 inset-ring-zinc-300 group-hover:bg-zinc-200 group-hover:inset-ring-zinc-400 ' +
  'dark:bg-zinc-400/15 dark:inset-ring-zinc-600 dark:group-hover:inset-ring-zinc-400';
const TILE_ICON_INACTIVE =
  'text-zinc-500 group-hover:text-zinc-700 dark:text-zinc-400 dark:group-hover:text-zinc-200';
const LABEL_INACTIVE =
  'text-zinc-700 group-hover:text-zinc-900 dark:text-zinc-300 dark:group-hover:text-white';

const THEME_COLORS = {
  sky: {
    tileActive:
      'bg-sky-100 inset-ring-[1.5px] inset-ring-sky-500 dark:bg-sky-400/20 dark:inset-ring-sky-400',
    tileIcon: 'text-sky-600 dark:text-sky-400',
    labelActive: 'text-sky-700 dark:text-white',
    subItemActive: 'text-sky-700 dark:text-sky-400',
    avatarBg: 'bg-sky-100 dark:bg-sky-500/20',
    avatarText: 'text-sky-600 dark:text-sky-400',
  },
  red: {
    tileActive:
      'bg-red-100 inset-ring-[1.5px] inset-ring-red-500 dark:bg-red-400/20 dark:inset-ring-red-400',
    tileIcon: 'text-red-600 dark:text-red-400',
    labelActive: 'text-red-700 dark:text-white',
    subItemActive: 'text-red-700 dark:text-red-400',
    avatarBg: 'bg-red-100 dark:bg-red-500/20',
    avatarText: 'text-red-600 dark:text-red-400',
  },
  amber: {
    tileActive:
      'bg-amber-100 inset-ring-[1.5px] inset-ring-amber-500 dark:bg-amber-400/20 dark:inset-ring-amber-400',
    tileIcon: 'text-amber-700 dark:text-amber-400',
    labelActive: 'text-amber-700 dark:text-white',
    subItemActive: 'text-amber-700 dark:text-amber-400',
    avatarBg: 'bg-amber-100 dark:bg-amber-500/20',
    avatarText: 'text-amber-700 dark:text-amber-400',
  },
} as const;

type ThemeColors = (typeof THEME_COLORS)[keyof typeof THEME_COLORS];

/**
 * Icon wrapped in a bordered, rounded tile with themed active / zinc-inactive treatment.
 * The parent row must carry `group` so hover brightening applies. `icon` is a lucide
 * element whose stroke follows currentColor, so the tile's text-* class colors it.
 */
function NavTile({
  icon,
  isActive,
  themeColors,
}: {
  icon: React.ReactNode;
  isActive: boolean;
  themeColors: ThemeColors;
}) {
  return (
    <span
      className={`${TILE_BASE} ${
        isActive
          ? `${themeColors.tileActive} ${themeColors.tileIcon}`
          : `${TILE_INACTIVE} ${TILE_ICON_INACTIVE}`
      }`}
    >
      {icon}
    </span>
  );
}

/**
 * A collapsible navigation section
 */
function CollapsibleNavSection({
  item,
  location,
  badgeCounts,
  themeColors,
}: {
  item: NavItem;
  location: { pathname: string };
  badgeCounts: Record<string, number>;
  themeColors: ThemeColors;
}) {
  const isChildActive = item.items?.some((child) => {
    if (child.items) {
      return child.items.some((grandchild) => grandchild.path === location.pathname);
    }
    return child.path === location.pathname;
  });

  const [isOpen, setIsOpen] = useState(!!isChildActive);

  // Auto-expand when navigating directly to a child route
  useEffect(() => {
    if (isChildActive) {
      setIsOpen(true);
    }
  }, [isChildActive]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    /* CONVENTION: Use Material Tailwind-style accordion for collapsible navigation */
    <div className="mb-1">
      <button
        onClick={toggleOpen}
        className="group flex items-center justify-between w-full px-2 py-1.5 rounded-lg transition-all duration-200 select-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2 focus-visible:ring-2 focus-visible:ring-sky-600/30"
      >
        <div className="flex items-center gap-3 min-w-0">
          <NavTile icon={item.icon} isActive={!!isChildActive} themeColors={themeColors} />
          <span
            className={`text-sm font-medium truncate ${isChildActive ? themeColors.labelActive : LABEL_INACTIVE}`}
          >
            {item.label}
          </span>
        </div>
        <svg
          className={`w-4 h-4 shrink-0 transition-transform ${isChildActive ? themeColors.tileIcon : 'text-zinc-400'} ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="ml-4 mt-1">
          <div className="p-0">
            {item.items?.map((child) => {
              if (!child.path) return null;

              return (
                <NavLink
                  key={child.label}
                  to={child.path}
                  className={`
                    group flex items-center gap-2 px-3 py-1.5 rounded-lg mb-1
                    transition-all duration-200 select-none relative
                    focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2
                    focus-visible:ring-2 focus-visible:ring-sky-600/30
                  `}
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`text-base shrink-0 ${isActive ? themeColors.subItemActive : 'text-zinc-500 group-hover:text-zinc-700 dark:text-zinc-400 dark:group-hover:text-zinc-200'}`}
                      >
                        {child.icon}
                      </span>
                      <span
                        className={`text-sm font-medium truncate ${isActive ? themeColors.subItemActive : LABEL_INACTIVE}`}
                      >
                        {child.label}
                      </span>
                      <NavBadge
                        count={badgeCounts[child.label]}
                        text={child.badgeText}
                        variant={child.badgeVariant}
                        color={child.badgeColor}
                        isExpanded={true}
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Profile avatar and name display — shared between clickable and static variants
 */
function ProfileDisplay({
  user,
  themeColors,
  shouldShowExpanded,
}: {
  user: UserDisplayData | null;
  themeColors: ThemeColors;
  shouldShowExpanded: boolean;
}) {
  const displayName = getUserDisplayName(user);

  return (
    <div className={`flex items-center ${shouldShowExpanded ? 'gap-3' : 'justify-center'}`}>
      <UserAvatar user={user} bgClass={themeColors.avatarBg} textClass={themeColors.avatarText} />
      {shouldShowExpanded && (
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-medium text-zinc-900 dark:text-white truncate">
            {displayName}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Badge color mapping - converts color name to Tailwind classes
 */
const BADGE_COLOR_CLASSES = {
  red: 'bg-red-600',
  blue: 'bg-sky-600' /* Changed from blue-500 to blue-600 for WCAG AA contrast (4.5:1) with white text */,
  green: 'bg-green-600',
  orange: 'bg-orange-600',
  gray: 'bg-zinc-600',
  purple: 'bg-purple-600',
} as const;

/**
 * Render a navigation badge based on variant and mode
 */
function NavBadge({
  count,
  text,
  variant = 'count',
  color = 'orange',
  isExpanded,
}: {
  count?: number;
  text?: string;
  variant?: 'count' | 'label' | 'dot';
  color?: string;
  isExpanded: boolean;
}) {
  // Defensive: Fallback to default color if lookup fails at runtime

  const colorClass =
    color in BADGE_COLOR_CLASSES
      ? BADGE_COLOR_CLASSES[color as keyof typeof BADGE_COLOR_CLASSES]
      : BADGE_COLOR_CLASSES.orange;

  // Dot variant — colored circle indicator, gated on count > 0 so callers
  // can opt the dot in/out by returning 1/0 from getCount.
  if (variant === 'dot' && count !== undefined && count > 0) {
    return isExpanded ? (
      <span className={`ml-auto shrink-0 h-2 w-2 rounded-full ${colorClass}`} />
    ) : (
      <span
        className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ${colorClass} border-2 border-white dark:border-zinc-900`}
      />
    );
  }

  // Label variant - text badge like "New" or "Beta"
  // aria-hidden: badge is visual decoration; the nav link label is sufficient for screen readers
  if (variant === 'label' && text) {
    return isExpanded ? (
      <span
        aria-hidden="true"
        className={`ml-auto shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${colorClass} text-white`}
      >
        {text}
      </span>
    ) : (
      <span
        aria-hidden="true"
        className={`absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${colorClass} text-white`}
      >
        {text}
      </span>
    );
  }

  // Count variant - numeric badge (default)
  if (variant === 'count' && count !== undefined && count > 0) {
    return isExpanded ? (
      <span
        className={`ml-auto shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${colorClass} text-white`}
      >
        {count}
      </span>
    ) : (
      <span
        className={`absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 text-xs font-semibold rounded-full ${colorClass} text-white`}
      >
        {count > 9 ? '9+' : count}
      </span>
    );
  }

  return null;
}

/**
 * Single toggle button to collapse/expand the sidebar.
 * Expanded → minimal (icons only, hover to expand).
 * Minimal/hidden → expanded (pinned open).
 */
function CollapseToggle({
  currentMode,
  onModeChange,
  isExpanded,
}: {
  currentMode: SidebarMode;
  onModeChange: (mode: SidebarMode) => void;
  isExpanded: boolean;
}) {
  const isPinned = currentMode === 'expanded';

  const toggle = () => {
    onModeChange(isPinned ? 'minimal' : 'expanded');
  };

  return (
    <div className={`flex ${isExpanded ? 'justify-end px-3' : 'justify-center px-1'} py-2`}>
      <button
        onClick={toggle}
        className="p-1.5 rounded-lg text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800/30 transition-colors
          focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2
          focus-visible:ring-2 focus-visible:ring-sky-600/30"
        title={isPinned ? 'Collapse sidebar' : 'Pin sidebar open'}
        aria-label={isPinned ? 'Collapse sidebar' : 'Pin sidebar open'}
      >
        {isPinned ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
      </button>
    </div>
  );
}

export interface SidebarProps {
  /** Navigation configuration to use (defaults to REGULAR_NAV_CONFIG) */
  navConfig?: typeof REGULAR_NAV_CONFIG;
  /** Theme variant for accent colors (defaults to "sky" for user portal) */
  theme?: 'sky' | 'red' | 'amber';
  /** Show profile section at bottom (defaults to true) */
  showProfile?: boolean;
  /** Allow clicking profile to open modal (defaults to true). Only applies when showProfile is true. */
  profileClickable?: boolean;
  /** Show subscription tier badge (defaults to true) */
  showSubscription?: boolean;
  /** User for profile section (optional - uses AuthContext if not provided) */
  user?: UserDisplayData | null;
}

/**
 * Primary navigation sidebar - Supports both user and admin portals
 *
 * Supports three display modes:
 * - hidden: Fully collapsed, expands on hover
 * - minimal: Shows only icons (avatar only for user profile), expands on hover
 * - expanded: Pinned open with full labels and text
 *
 * Features:
 * - Hover expansion for hidden/minimal modes (no click required)
 * - Bottom-anchored user section with mode controls
 * - Persisted mode preference via localStorage
 * - Smooth transitions between states
 * - Configurable for different contexts (user portal, admin portal)
 *
 * @example
 * // User portal (default)
 * <Sidebar />
 *
 * @example
 * // Admin portal
 * <Sidebar navConfig={ADMIN_NAV_CONFIG} title="Admin Portal" theme="amber" profileClickable={false} showSubscription={false} />
 */
export default function Sidebar({
  navConfig = REGULAR_NAV_CONFIG,
  theme = 'sky',
  showProfile = true,
  profileClickable = true,
  showSubscription = true,
  user: userProp,
}: SidebarProps = {}) {
  const location = useLocation();
  const { mode, setMode, isExpanded, isHovering, setIsHovering } = useSidebarContext();
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  // Detect mobile screen size (< lg breakpoint = 1024px)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Safe context access — returns undefined when no AuthProvider ancestor (admin portal)
  const authContext = useContext(AuthContext);
  const authUser = authContext?.user ?? null;
  const user = userProp === undefined ? authUser : userProp || null;

  // Sidebar owns its navigation evaluation - re-evaluates on context changes
  const { items, badgeCounts, loading: navLoading } = useNavigationItems(navConfig);

  // On mobile, always use expanded mode (ignore desktop mode settings)
  // Mobile sidebar is controlled by Layout's mobile menu button, not mode settings
  const effectiveMode = isMobile ? 'expanded' : mode;
  const effectiveIsExpanded = isMobile ? true : isExpanded;

  // Determine actual display state (expand on hover for hidden/minimal modes)
  const shouldShowExpanded = effectiveIsExpanded || (isHovering && !effectiveIsExpanded);

  // Theme-based color classes
  const themeColors = THEME_COLORS[theme];

  // Calculate width based on mode and hover state
  // On mobile, always full width (controlled by Layout's transform)
  const width = (() => {
    if (isMobile) return 'w-64'; // Always full width on mobile
    if (effectiveMode === 'hidden' && !isHovering) return 'w-0';
    if (shouldShowExpanded) return 'w-64';
    return 'w-20';
  })();

  return (
    <>
      <div
        className={`
          h-full ${width} transition-all duration-300 ease-in-out
          ${!isMobile && effectiveMode === 'hidden' && !isHovering ? 'opacity-0' : 'opacity-100'}
          flex flex-col
          shadow-xl shadow-blue-zinc-900/5 dark:shadow-zinc-900/20 rounded-none
          bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md
          border-r border-zinc-200 dark:border-zinc-800
        `}
        onMouseEnter={() => !isMobile && setIsHovering(true)}
        onMouseLeave={() => !isMobile && setIsHovering(false)}
      >
        {/* App branding */}
        <div className={`shrink-0 mb-4 ${shouldShowExpanded ? 'px-4 pt-4' : 'p-1 pt-2'}`}>
          <div
            className={`flex items-center min-w-0 ${shouldShowExpanded ? 'gap-3' : 'justify-center px-2 py-2'}`}
          >
            <img
              src="/coldsea-logo.svg"
              alt=""
              className={`shrink-0 dark:invert transition-all duration-300 ${shouldShowExpanded ? 'h-5' : 'h-4'}`}
              aria-hidden="true"
            />
            {shouldShowExpanded && (
              <span className="text-2xl font-bold text-zinc-900 dark:text-white truncate">
                Deploy
              </span>
            )}
          </div>
        </div>

        {/* Navigation items - flex-1 to take available space */}
        <nav className="flex-1 overflow-y-auto min-h-0">
          {navLoading ? (
            <div className={shouldShowExpanded ? 'p-2 space-y-2' : 'p-1 space-y-2'}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className={`animate-pulse rounded-lg bg-zinc-200 dark:bg-zinc-700 ${shouldShowExpanded ? 'h-9' : 'h-9 mx-auto w-9'}`}
                />
              ))}
            </div>
          ) : (
            <div className={shouldShowExpanded ? 'p-2' : 'p-1'}>
              {items.map((item) => {
                if (item.items) {
                  // Collapsed: show parent icon linking to first child path
                  if (!shouldShowExpanded) {
                    const firstChildPath = item.items.find((c) => c.path)?.path;
                    if (!firstChildPath) return null;
                    const isChildActive = item.items.some((child) =>
                      child.items
                        ? child.items.some((gc) => gc.path === location.pathname)
                        : child.path === location.pathname,
                    );
                    return (
                      <NavLink
                        key={item.label}
                        to={firstChildPath}
                        title={item.label}
                        className="group flex items-center justify-center px-2 py-1.5 rounded-lg mb-1 transition-all duration-200 select-none relative focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2 focus-visible:ring-2 focus-visible:ring-sky-600/30"
                      >
                        <NavTile icon={item.icon} isActive={isChildActive} themeColors={themeColors} />
                      </NavLink>
                    );
                  }
                  return (
                    <CollapsibleNavSection
                      key={item.label}
                      item={item}
                      location={location}
                      badgeCounts={badgeCounts}
                      themeColors={themeColors}
                    />
                  );
                }
                if (item.path) {
                  const badgeCount = badgeCounts[item.label];

                  return (
                    <NavLink
                      key={item.label}
                      to={item.path}
                      title={!shouldShowExpanded ? item.label : undefined}
                      className={`
                        group flex items-center ${shouldShowExpanded ? 'gap-3 px-2 py-1.5' : 'justify-center px-2 py-1.5'} rounded-lg mb-1
                        transition-all duration-200 select-none relative
                        focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2
                        focus-visible:ring-2 focus-visible:ring-sky-600/30
                      `}
                    >
                      {({ isActive }) => (
                        <>
                          <NavTile icon={item.icon} isActive={isActive} themeColors={themeColors} />
                          {shouldShowExpanded && (
                            <span
                              className={`text-sm font-medium truncate ${isActive ? themeColors.labelActive : LABEL_INACTIVE}`}
                            >
                              {item.label}
                            </span>
                          )}
                          <NavBadge
                            count={badgeCount}
                            text={item.badgeText}
                            variant={item.badgeVariant}
                            color={item.badgeColor}
                            isExpanded={shouldShowExpanded}
                          />
                        </>
                      )}
                    </NavLink>
                  );
                }
                return null;
              })}
            </div>
          )}
        </nav>

        {/* Bottom section - User info and mode controls (flex-shrink-0 to prevent squishing) */}
        <div className="shrink-0 border-t border-zinc-200 dark:border-zinc-800">
          {/* Task guide widget */}
          <div className="pt-3">
            <TaskGuideWidget isExpanded={shouldShowExpanded} />
          </div>

          {/* Collapse/expand toggle - hidden on mobile (mode is forced to expanded) */}
          {!isMobile && (
            <CollapseToggle
              currentMode={mode}
              onModeChange={setMode}
              isExpanded={shouldShowExpanded}
            />
          )}

          {/* Subscription badge - full when expanded, icon-only when collapsed */}
          {showSubscription && (
            <div className={`${shouldShowExpanded ? 'px-3' : 'px-2 flex justify-center'} pb-3`}>
              {shouldShowExpanded ? (
                <SubscriptionBadge size="sm" className="w-full justify-center" />
              ) : (
                <SubscriptionBadge size="sm" showIcon iconOnly />
              )}
            </div>
          )}

          {/* User profile button */}
          {showProfile && (
            <div className={`${shouldShowExpanded ? 'px-3' : 'px-2'} pb-4`}>
              {profileClickable ? (
                <button
                  onClick={() => setProfileModalOpen(true)}
                  className={`
                    w-full ${shouldShowExpanded ? 'px-3 py-2' : 'px-2 py-2'} bg-zinc-100 dark:bg-white/5 rounded-lg
                    hover:bg-zinc-200 dark:hover:bg-white/10 transition-colors cursor-pointer select-none
                    focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2
                    focus-visible:ring-2 focus-visible:ring-sky-600/30
                  `}
                  title={!shouldShowExpanded ? 'Edit profile' : undefined}
                >
                  <ProfileDisplay
                    user={user}
                    themeColors={themeColors}
                    shouldShowExpanded={shouldShowExpanded}
                  />
                </button>
              ) : (
                <div
                  className={`
                    w-full ${shouldShowExpanded ? 'px-3 py-2' : 'px-2 py-2'} bg-zinc-100 dark:bg-white/5 rounded-lg
                  `}
                >
                  <ProfileDisplay
                    user={user}
                    themeColors={themeColors}
                    shouldShowExpanded={shouldShowExpanded}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Profile Modal */}
      {showProfile && profileClickable && (
        <ProfileModal
          isOpen={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          onUpdate={() => {
            // Profile updated - user data will be refreshed by the modal
          }}
        />
      )}
    </>
  );
}
